import * as R from 'ramda';
import { packDisallowChange } from '../../../../grids/utils/packs.utils';
import { AvailabilityEnum } from '../../../../products/ProductTypes';
import { isPreOrderOrMaterial } from '../../../../products/utils/availability.utils';
import { OrderView } from '../../../ordersView.types';
import {
  OrderItemListGridItemDTO,
  OrderProductGridItem,
  UpdateItemFormatWithSize,
} from '../../orderProducts.types';
import { OrderProductForGridContainer } from '../grid/components/orderProductGrid/components/grid/types/orderProductGridContainer.types';
import { ShouldDisableProductInputProps } from '../grid/components/orderProductGrid/types/productGrid.types';

type DisableStockInput = {
  availableAmount?: number | null;
  quantity?: number;
  available?: boolean;
  productSizeActive: boolean;
};

interface OrderItemInputWithError {
  availability: AvailabilityEnum;
  availableForReadyForDeliveryWithoutStock: boolean;
  quantity?: number;
  available: boolean;
  productSizeActive: boolean;
  orderViewMode?: OrderView;
}

interface IShowStock {
  availability: AvailabilityEnum;
  viewMode?: OrderView;
  availableForSale: boolean;
  stockValue?: number | null;
}

export const showStock = ({
  availability,
  viewMode,
  availableForSale,
  stockValue,
}: IShowStock): boolean => {
  if (R.isNil(stockValue)) {
    return false;
  }
  if (viewMode === OrderView.FINISHED) {
    return false;
  }
  switch (availability) {
    case AvailabilityEnum.EMPTY:
    case AvailabilityEnum.INFINITE_PRE_ORDER:
      return false;
    case AvailabilityEnum.MATERIAL_PRE_ORDER:
    case AvailabilityEnum.FUTURE_STOCK:
    case AvailabilityEnum.READY_FOR_DELIVERY:
      return availableForSale;
  }
};

export const isInputWithError = ({
  availability,
  availableForReadyForDeliveryWithoutStock,
  quantity,
  available,
  productSizeActive,
  orderViewMode,
}: OrderItemInputWithError): boolean => {
  if (
    !quantity ||
    quantity === 0 ||
    availability === AvailabilityEnum.EMPTY ||
    orderViewMode === OrderView.FINISHED
  ) {
    return false;
  }

  if (!productSizeActive) {
    return true;
  }

  if (isPreOrderOrMaterial(availability)) {
    return !available;
  }

  return !availableForReadyForDeliveryWithoutStock || !available;
};

export const mustDisableStockInput = ({
  availableAmount,
  quantity,
  available,
  productSizeActive,
}: DisableStockInput) => {
  if (quantity && quantity > 0) {
    return false;
  }

  return (!quantity && availableAmount === 0) || !available || !productSizeActive;
};

export const productUrlBuilder = ({
  productColorId,
  productId,
}: {
  productColorId: string;
  productId: string;
}) => {
  const urlParts: string[] = [`/product/${productId}`];
  urlParts.push(`/color/${productColorId}`);

  return urlParts.join('');
};

const removeUnusedSkus = (
  currentGridValue: OrderProductGridItem,
  copiedGridValue: OrderProductGridItem,
) => {
  if (!R.isNil(copiedGridValue?.orderItemQuantity)) {
    const productHasSameSizeName =
      currentGridValue.productSizeName === copiedGridValue.productSizeName;
    const productIsAvailable = copiedGridValue.available && currentGridValue.available;

    if (productHasSameSizeName && productIsAvailable) {
      return currentGridValue;
    }
  }
};

const verifyMaxStockGridItem = (
  newGridValue: OrderProductGridItem,
  copiedGridValue: OrderProductGridItem,
) => {
  let maxStockAvailable = false;
  if (copiedGridValue?.orderItemQuantity && newGridValue?.stock?.availableAmount) {
    maxStockAvailable = copiedGridValue?.orderItemQuantity >= newGridValue?.stock?.availableAmount;
  }
  return maxStockAvailable;
};

const formatGridItem = (
  newGridItem: OrderProductGridItem,
  copiedGridValue: OrderProductGridItem,
  product: OrderProductForGridContainer,
  maxStockAvailable: boolean,
): UpdateItemFormatWithSize => ({
  id: newGridItem?.orderItemId,
  skuId: newGridItem?.skuId,
  deliveryWindowId: product.deliveryWindowId,
  availability: product.availability,
  productColorId: product.productColorId,
  quantity: maxStockAvailable
    ? newGridItem?.stock?.availableAmount || 0
    : copiedGridValue?.orderItemQuantity || 0,
  productSizeId: newGridItem.productSizeId,
});

const mergeOrderItemListResponseIntoGridItem = (
  currentGridItem: OrderProductGridItem,
  response: OrderItemListGridItemDTO,
): OrderProductGridItem => ({
  ...currentGridItem,
  orderItemId: response.orderItemId,
  orderConditionId: response.orderConditionId,
  orderItemQuantity: response.orderItemQuantity || 0,
  orderWholeSalePrice: response.orderWholeSalePrice || 0,
  orderPromotionalPrice: response.orderPromotionalPrice,
  priceComposition: response.priceComposition,
});

const createNewGridWithPasteItems = (
  currentGridItem: OrderProductGridItem,
  newGrid: OrderItemListGridItemDTO[],
) => {
  const gridItem = newGrid.find((newGridItem) => newGridItem.skuId === currentGridItem.skuId);
  if (gridItem) {
    return mergeOrderItemListResponseIntoGridItem(currentGridItem, gridItem);
  }
  return currentGridItem;
};

export const updateProductGrid = (
  currentGrid: OrderProductGridItem[],
  availability: AvailabilityEnum,
  newGrid: OrderItemListGridItemDTO[] = [],
  deliveryWindowId?: string | null,
): OrderProductGridItem[] => {
  const filteredGrid = newGrid.filter((item) => {
    const deliveryWindowValidate = item.deliveryWindowId
      ? item.deliveryWindowId === deliveryWindowId
      : true;

    return item.availability === availability && deliveryWindowValidate;
  });

  const returnedGrid = currentGrid.map((currentGridItem) =>
    createNewGridWithPasteItems(currentGridItem, filteredGrid),
  );

  return returnedGrid as OrderProductGridItem[];
};

export const shouldDisableProductInput = ({
  error,
  gridItem,
  availability,
  orderViewMode,
  createOrderItem,
  allowChangePreOrderGrid,
  allowChangeImmediateGrid,
}: ShouldDisableProductInputProps) => {
  if (orderViewMode === OrderView.FINISHED) {
    return true;
  }

  const stockDisabled = mustDisableStockInput({
    availableAmount: gridItem.stock?.availableAmount,
    quantity: gridItem.orderItemQuantity || createOrderItem?.quantity,
    available: gridItem?.available,
    productSizeActive: gridItem.productSizeActive,
  });

  const disableGridItem = !error && (!gridItem?.available || !gridItem.productSizeActive);

  const mustDisablePackGrid = packDisallowChange(
    availability,
    allowChangePreOrderGrid,
    allowChangeImmediateGrid,
  );

  switch (availability) {
    case AvailabilityEnum.READY_FOR_DELIVERY:
    case AvailabilityEnum.MATERIAL_PRE_ORDER:
    case AvailabilityEnum.FUTURE_STOCK:
      return stockDisabled || mustDisablePackGrid;
    case AvailabilityEnum.INFINITE_PRE_ORDER:
    case AvailabilityEnum.EMPTY:
      return disableGridItem || mustDisablePackGrid;
    default:
      return true;
  }
};

export const formatNewGrid = (
  product: OrderProductForGridContainer,
  currentGrid: OrderProductGridItem[],
  copiedGrid: OrderProductGridItem[],
): UpdateItemFormatWithSize[] => {
  const newGrid = copiedGrid
    .reduce((itemsToUpdate: UpdateItemFormatWithSize[], copiedGridValue) => {
      const newGridValue = currentGrid.find((currentGridValue) =>
        removeUnusedSkus(currentGridValue, copiedGridValue),
      );
      if (newGridValue) {
        const maxStockAvailable = verifyMaxStockGridItem(newGridValue, copiedGridValue);
        const formattedGridItem = formatGridItem(
          newGridValue,
          copiedGridValue,
          product,
          maxStockAvailable,
        );
        itemsToUpdate.push(formattedGridItem);
      }

      return itemsToUpdate;
    }, [])
    .filter((newGridItem) => newGridItem !== null);

  return newGrid;
};
